import React from 'react';
import { useTranslation } from 'react-i18next';
import { differenceInCalendarDays, parseISO } from 'date-fns';

import { useTheme } from '@/theme';
import { Flex, Spinner, Tag, Text } from '@/components/ui';
import { i18nKeys } from '@/i18n/i18nKeyGen';
import { useGetSubscription } from '@/api/company/useSubscription';
import type { CompanySubscriptionEntry } from '@packages/types/company';

interface CompanySubscriptionHistorySectionProps {
  companyId: number;
}

const CompanySubscriptionHistorySection: React.FC<CompanySubscriptionHistorySectionProps> = ({
  companyId,
}) => {
  const { t } = useTranslation(['company', 'common']);
  const { theme } = useTheme();

  const { data, isLoading } = useGetSubscription(companyId, {
    enabled: !!companyId,
  });

  const current = data?.current ?? null;
  const history = data?.history ?? [];

  const durationText = (entry: CompanySubscriptionEntry): string =>
    entry.durationMonths != null
      ? t(i18nKeys.company.subscription_duration_months, { count: entry.durationMonths })
      : t(i18nKeys.company.subscription_duration_days, { count: entry.durationDays });

  const statusLabel = (status: CompanySubscriptionEntry['status']): string => {
    if (status === 'active') return t(i18nKeys.company.subscription_active);
    if (status === 'expired') return t(i18nKeys.company.subscription_expired);
    return t(i18nKeys.company.subscription_canceled);
  };

  const statusVariant = (
    status: CompanySubscriptionEntry['status'],
  ): 'success' | 'warning' | 'error' => {
    if (status === 'active') return 'success';
    if (status === 'expired') return 'error';
    return 'warning';
  };

  const isCurrentSubscriptionRow = (entry: CompanySubscriptionEntry): boolean =>
    current != null && entry.id === current.id;

  const sectionTitleStyle: React.CSSProperties = {
    marginBottom: theme.spacing[3],
  };

  return (
    <section>
      <Text size="md" weight="semibold" style={sectionTitleStyle}>
        {t(i18nKeys.company.subscription_history)}
      </Text>
      {isLoading ? (
        <Flex align="center" justify="center" style={{ padding: theme.spacing[6] }}>
          <Spinner size="sm" />
        </Flex>
      ) : history.length === 0 ? (
        <Text size="sm" color={theme.colors.text.secondary}>
          {t(i18nKeys.company.subscription_no_history)}
        </Text>
      ) : (
        <table
          style={{
            width: '100%',
            tableLayout: 'fixed',
            borderCollapse: 'collapse',
            fontSize: theme.fontSize.sm,
          }}
        >
          <colgroup>
            <col style={{ width: '28%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: 'auto' }} />
            <col style={{ width: '18%' }} />
          </colgroup>
          <thead style={{ backgroundColor: theme.colors.surface.header.default }}>
            <tr>
              <th style={{ padding: theme.spacing[4], textAlign: 'center' }}>
                {t(i18nKeys.company.subscription_period)}
              </th>
              <th style={{ padding: theme.spacing[4], textAlign: 'center' }}>
                {t(i18nKeys.company.subscription_device_limit)}
              </th>
              <th style={{ padding: theme.spacing[4], textAlign: 'left' }}>
                {t(i18nKeys.company.subscription_memo)}
              </th>
              <th style={{ padding: theme.spacing[4], textAlign: 'center' }}>
                {t(i18nKeys.company.subscription_status)}
              </th>
            </tr>
          </thead>
          <tbody>
            {history.map(entry => (
              <tr
                key={entry.id}
                style={{ borderBottom: `1px solid ${theme.colors.border.container}` }}
              >
                <td style={{ padding: theme.spacing[4], textAlign: 'center' }}>
                  <Text size="sm">
                    {entry.periodStart} ~ {entry.periodEnd} ({durationText(entry)})
                  </Text>
                </td>
                <td style={{ padding: theme.spacing[4], textAlign: 'center' }}>
                  <Text size="sm">{entry.deviceLimit}</Text>
                </td>
                <td style={{ padding: theme.spacing[4], textAlign: 'left' }}>
                  <Text
                    size="sm"
                    color={theme.colors.text.secondary}
                    truncate
                    title={entry.memo || ''}
                  >
                    {entry.memo || '-'}
                  </Text>
                </td>
                <td style={{ padding: theme.spacing[4], textAlign: 'center' }}>
                  <Flex direction="column" align="center" gap={1}>
                    <Tag variant={statusVariant(entry.status)} size="sm">
                      {statusLabel(entry.status)}
                    </Tag>
                    {isCurrentSubscriptionRow(entry) &&
                      entry.status === 'active' &&
                      current && (
                        <Text size="xs" color={theme.colors.text.secondary}>
                          {current.periodEnd} (
                          {t(i18nKeys.company.subscription_d_day, {
                            days: Math.max(
                              differenceInCalendarDays(parseISO(current.periodEnd), new Date()),
                              0,
                            ),
                          })}
                          )
                        </Text>
                      )}
                    {isCurrentSubscriptionRow(entry) &&
                      (entry.status === 'expired' || entry.status === 'canceled') &&
                      current?.graceUntil && (
                        <Text size="xs" color={theme.colors.status.danger.stroke.text}>
                          {t(i18nKeys.company.subscription_delete_scheduled, {
                            date: current.graceUntil,
                            days: Math.max(
                              differenceInCalendarDays(parseISO(current.graceUntil), new Date()),
                              0,
                            ),
                          })}
                        </Text>
                      )}
                  </Flex>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
};

export default CompanySubscriptionHistorySection;
